<template>
  <section class="template-detail-wrapper">
    <TopHeader class="template-detail-wrapper__top-header" />
    <div class="template-detail">
      <Breadcrumb :template-name="calData?.name" />

      <div class="template-detail__container">
        <VideoContainer
          :template-id="templateId"
          :preview-url="calData?.preview_m3u8_url"
          :full-video-url="calData?.full_video_url"
          :show-ai-icon="calData?.show_ai_icon"
          :cover-url="calData?.cover_url"
          :start-time="currentTime"
          @time-update="handleTimeUpdate"
        />
        <TemplateInfo
          :template-name="calData?.name"
          :is-hot="isHot"
          :is-new="calData?.is_new"
          :description="calData?.message"
          :new-tag-ids="calData?.new_tag_ids"
          :avatar-url="avatarUrl"
          :avatar-name="avatarName"
          :publish-time="publishTime"
          :copyright-limited="calData?.copyright_limited"
          :is-star-personal="calData?.is_star_personal"
          :template-id="templateId"
          @avatar-click="handAvatarClick"
          @use-template="useTemplate(calData)"
          @collect-change="onCollectChange"
        />
      </div>

      <div class="template-detail__card-segments">
        <CardSegments
          :segments="calData?.camera_moment_preview || []"
          v-model:currentTime="currentTime"
        />
      </div>

      <div class="template-detail__divider"></div>

      <!-- 脚本文本 -->
      <SegmentText v-if="calData?.segment_text" :text="calData?.segment_text" />
    </div>

    <div class="template-detail__divider template-detail__divider--secondary"></div>

    <div class="template-detail__similar">
      <Suggest :templateId="templateId" />
    </div>
    <StartRightNow :handler="() => {}" />
    <AppFooter></AppFooter>
  </section>
</template>

<script lang="ts" setup>
import { useTime } from '@/hooks/useTime';
import { isEmpty, last } from 'lodash-es';
import { useI18n } from 'vue-i18n';
import { addCollect, cancelCollect, getTemplate } from '~/api';
import type { Template } from '~/api/interface';
import { PageKey } from '~/constants/page';

import StartRightNow from '~/design/widgets/start-right-now/index.vue';
import TopHeader from '~/design/widgets/top-header/index.vue';
import { useTemplate } from '~/features/template/components/info';
import { useWatchPageSeoData } from '~/hooks/useSeo';
import { useStartPatch } from '~/hooks/useStarPatch';
import { isChinese } from '~/utils/area';

import { MODEL_TYPE } from '@rendora/common/types/enums';
import Breadcrumb from './Breadcrumb.vue';
import CardSegments from './CardSegments.vue';
import SegmentText from './SegmentText.vue';
import Suggest from './Suggest.vue';
import TemplateInfo from './TemplateInfo.vue';
import VideoContainer from './VideoContainer.vue';

const AppFooter = defineAsyncComponent(() =>
  isChinese()
    ? import('~/design/widgets/bottom-footer/footer-cn.vue').then(m => m.default)
    : import('~/design/widgets/bottom-footer/footer-en.vue').then(m => m.default),
);

const { timeFormat } = useTime();
const { t: $t } = useI18n();

const route = useRoute();
const userInfo = useUserInfo();

const currentTime = ref(0);

const templateId = computed(() => Number(last(String(route.params.id).split('-'))));

function autoSuffix(t: string) {
  const suffix = isChinese() ? `-${$t('you-yan')}AI` : `-${$t('you-yan')}`;
  if (t.endsWith(suffix)) {
    return t;
  }

  return t + suffix;
}

function getSeoDescriptionFromScript(text: string) {
  const str = text.split('\n')[0] || '';
  return str.slice(0, isChinese() ? 30 : 60);
}

const { data: templateData } = useAsyncData(async () => {
  const { data } = await getTemplate({
    template_id: templateId.value,
    query_star: false, // 保持SSR和CSR一致，统一在客户端通过useStartPatch补丁
  });

  if (!isEmpty(data)) {
    data.seo_title = data.seo_title || autoSuffix(data.name);
    data.seo_description =
      data.seo_description || data.message || getSeoDescriptionFromScript(data.segment_text);
    // data.query_star = true;
  }

  return data as Template;
});

watch(
  () => templateData.value,
  data => {
    if (data && !data?.id) {
      throw new Error(`no template found for id: ${templateId.value}`);
    }
  },
  { immediate: true, flush: 'sync' },
);

const { calData } = useStartPatch(templateData, MODEL_TYPE.TASK_TEMPLATE);

useWatchPageSeoData(templateData);

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: templateData.value?.seo_title,
  description: templateData.value?.seo_description,
  contentUrl: templateData.value?.preview_m3u8_url || templateData.value?.full_video_url,
  uploadDate: templateData.value?.create_time,
  thumbnailUrl: templateData.value?.cover_url,
}));

function changeUrl() {
  if (!import.meta.client || !templateData.value) {
    return;
  }

  const memoizedPath = useMemoizedPath();
  if (memoizedPath.value === null) {
    memoizedPath.value = new URL(window.location.href).pathname;
  }

  const { id } = templateData.value;
  memoizedPath.value = new URL(window.location.href).pathname;
  const link = `/template/${id}.html`;
  history.replaceState({}, '', link);
}

watch(() => templateData.value, changeUrl);

const avatarUrl = computed(() =>
  !templateData.value
    ? ''
    : transformImage(templateData.value?.avatar_url || templateData.value?.user_logo_url, {
        height: 36,
      }),
);

const avatarName = computed(() =>
  !templateData.value ? '' : templateData.value?.name_for_show || templateData.value?.username,
);

const publishTime = computed(() => {
  return timeFormat(templateData.value?.online_time || templateData.value?.create_time);
});

// 是否热门
const isHot = computed(() => {
  if (!templateData.value || !templateData.value.tags) {
    return false;
  }

  return templateData.value?.tags?.includes($t('re-men'));
});

const location = useRequestURL();

const handAvatarClick = () => {
  if (templateData.value) {
    const router = useRouter();
    router.push({
      name: PageKey.PERSONAL_PAGE,
      params: {
        key: templateData.value.homepage_org_id || 0,
      },
    });
  }
};

const onCollectChange = () => {
  if (userInfo.value && calData.value) {
    if (!calData.value.is_star_personal) {
      addCollect({
        model_id: calData.value.id,
        model_type: COLLECTION_MAP.PIECES_TEMPLATE,
        source: COLLECTION_SOURCE.YOUYAN,
      });
      message.success($t('shou-cang-cheng-gong'));
    } else {
      cancelCollect({
        model_ids: [calData.value.id],
        model_type: COLLECTION_MAP.PIECES_TEMPLATE,
        source: COLLECTION_SOURCE.YOUYAN,
      });
      message.success($t('qu-xiao-shou-cang-cheng-gong'));
    }
    calData.value.is_star_personal = !calData.value.is_star_personal;
  } else {
    navigateTo(`/login?redirect=${encodeURIComponent(encodeURIComponent(location?.href || ''))}`, {
      external: true,
    });
  }
};

provideResourceLocation({
  source: SOURCE.TEMPLATE_CENTER,
});

watch(
  () => templateData.value,
  val => {
    if (val && import.meta.client) {
      trackEvent(TRACK_EVENT.PLAY_TEMPLATE_VIDEO, {
        source: val.source,
        template_id: val.id,
        template_title: val.title,
      });
    }
  },
);

const handleTimeUpdate = (time: number) => {
  currentTime.value = time;
};
</script>

<style scoped lang="scss">
.template-detail__card-segments {
  width: calc(100% - 416px);
  margin-top: 24px;
}

.template-detail__container {
  display: flex;
  gap: 32px;
}

// 包裹容器
.template-detail-wrapper {
  padding-top: 64px;
  position: relative;
}

// 主容器
.template-detail {
  width: clamp(1200px, 1200px + (100vw - 1600px) * 0.75, 1440px);
  margin: 0 auto;
  position: relative;
}

.template-detail-wrapper__top-header {
  background-color: white;
}

// 分割线
.template-detail__divider {
  margin: 24px 0;
  width: 100%;
  height: 1px;
  background-color: var(--x-back-4);
}

.template-detail__divider--secondary {
  margin: 70px auto 160px;
}
</style>
